import { create } from 'zustand';
import errorHandler from '../utils/error-handler.util';
import { IApiResponse, IPagination } from '../models/resource.model';
import {
  IBeneficiaryPayload,
  IGetBeneficiariesParam,
  IGetBeneficiary,
  PaymentMethodEnum,
} from '../models/beneficiary.model';
import createAxiosInstance from '../services/global/axiosInstance';
import useRateStore from './rate';

const axiosInstance = createAxiosInstance();

export interface IBeneficiaryStore {
  loading: boolean;
  step: number;
  selectedPaymentMethod: string;
  beneficiaryPayload: IBeneficiaryPayload;
  beneficiaries: IGetBeneficiary[];
  setBeneficiaries: (beneficiaries: IGetBeneficiary[]) => void;
  beneficiariesPagination: IPagination;
  setBeneficiaryPagination: (paginationObj: IPagination) => void;
  setBeneficiaryPayload: (updatedPayload: IBeneficiaryPayload) => void;
  resetBeneficiaryPayload: () => void;
  goNext: () => void;
  prev: () => void;
  resetStep: () => void;
  saveBeneficiary: () => Promise<IApiResponse | null>;
  validateIban: (iban: string) => Promise<IApiResponse | null>;
  validateRoutingNumber: (
    routingNumber: string,
  ) => Promise<IApiResponse | null>;
  validateSwiftCode: (swiftCode: string) => Promise<IApiResponse | null>;
  deleteBeneficiary: (id: string) => Promise<IApiResponse | null>;
  getAllBeneficiary: (
    params: IGetBeneficiariesParam,
  ) => Promise<IApiResponse | null>;
}

const initialBeneficiaryPayload: IBeneficiaryPayload = {
  accountHolderName: '',
  bankName: '',
  beneficiaryEmail: '',
  currencyCode: '',
  transactionType: '',
  paymentMethod: '',
  firstName: '',
  lastName: '',
  businessName: '',
  accountOwnerType: 1,
  swiftPayment: {
    bankAddress: {
      streetLine1: '',
      streetLine2: '',
      city: '',
      state: '',
      postalCode: '',
      countryCode: '',
    },
    ibanAccount: {
      countryCode: '',
      bic: '',
      ibanAccount: '',
    },
    category: '',
  },
  localAccountPayment: {
    accountNumber: '',
    routingNumber: '',
    checkingOrSavings: '',
  },
  unKnownPayment: {
    bic: '',
    accountNumber: '',
  },
  ibanPayment: {
    countryCode: '',
    bic: '',
    ibanAccount: '',
  },
  paymentType: 1,
  beneficiaryAddress: {
    streetLine1: '',
    streetLine2: '',
    city: '',
    state: '',
    postalCode: '',
    countryCode: '',
    fullAddress: '',
  },
};

const useBeneficiaryStore = create<IBeneficiaryStore>((set, get) => ({
  loading: false,
  step: 1,
  selectedPaymentMethod: '',
  beneficiaryPayload: { ...initialBeneficiaryPayload },
  beneficiaries: [],
  setBeneficiaries: (beneficiaries: IGetBeneficiary[]) => {
    set({ beneficiaries: beneficiaries });
  },
  beneficiariesPagination: {
    currentPage: 1,
    totalRecords: 0,
    currentPageSize: 10,
    totalPages: 0,
    hasNextPage: false,
    hasPrevious: true,
  },
  setBeneficiaryPayload: (updatedPayload: IBeneficiaryPayload) => {
    set({ beneficiaryPayload: { ...updatedPayload } });
  },
  setBeneficiaryPagination: (paginationObj: IPagination) => {
    set({ beneficiariesPagination: { ...paginationObj } });
  },
  resetBeneficiaryPayload: () => {
    set({ beneficiaryPayload: { ...initialBeneficiaryPayload } });
  },
  goNext: () => {
    const { step } = get();
    if (step < 3) set({ step: step + 1 });
  },
  prev: () => {
    const { step } = get();
    if (step > 1) set({ step: step - 1 });
  },
  resetStep: () => {
    set({ step: 1 });
  },
  saveBeneficiary: async () => {
    set({ loading: true });

    const beneficiary = get().beneficiaryPayload;
    const { activeCountries: countries } = useRateStore.getState();
    const beneficiaryCountry = countries.filter(
      (item) => item.iso2 === beneficiary.beneficiaryAddress.countryCode,
    );

    const bankCountry = countries.filter(
      (item) =>
        item.iso2 === beneficiary.swiftPayment?.bankAddress?.countryCode ||
        beneficiary.ibanPayment.countryCode,
    );

    const payload: any = {
      ...beneficiary,
      beneficiaryAddress: {
        ...beneficiary.beneficiaryAddress,
        countryCode: beneficiaryCountry.length
          ? beneficiaryCountry[0].iso3
          : beneficiary.beneficiaryAddress.countryCode,
      },
      swiftPayment: {
        ...beneficiary.swiftPayment,
        bankAddress: {
          ...beneficiary.swiftPayment?.bankAddress,
          countryCode: bankCountry.length ? bankCountry[0].iso3 : '',
        },
        ibanAccount: {
          ...beneficiary.swiftPayment?.ibanAccount,
          countryCode: bankCountry.length ? bankCountry[0].iso3 : '',
        },
      },
      ibanPayment: {
        ...beneficiary.ibanPayment,
        countryCode: bankCountry.length ? bankCountry[0].iso3 : '',
      },
    };

    if (payload.paymentMethod === 'USDLocal') {
      delete payload.ibanPayment;
      delete payload.swiftPayment;
      delete payload.unKnownPayment;
      payload.paymentType = PaymentMethodEnum.USDLocal;
    } else if (payload.paymentMethod === 'SWIFT') {
      delete payload.ibanPayment;
      delete payload.localAccountPayment;
      delete payload.unKnownPayment;
      payload.paymentType = PaymentMethodEnum.SWIFT;
    } else if (payload.paymentMethod === 'SWIFTUnknown') {
      delete payload.ibanPayment;
      delete payload.localAccountPayment;
      delete payload.swiftPayment;
      payload.paymentType = PaymentMethodEnum.SWIFTUNKNOWN;
    } else {
      delete payload.swiftPayment;
      delete payload.localAccountPayment;
      delete payload.unKnownPayment;
      payload.paymentType = PaymentMethodEnum.IBAN;
    }

    try {
      const response = await axiosInstance.post(
        '/corporate/save-beneficiary',
        payload,
      );
      set({ loading: false });
      await get().getAllBeneficiary({
        page: 1,
        pageSize: 10,
        accountHolderName: '',
        currencyCode: '',
      });
      return response?.data;
    } catch (err: unknown) {
      errorHandler(err);
      set({ loading: false });
      return null;
    }
  },
  getAllBeneficiary: async ({
    page = 1,
    pageSize = 10,
    accountHolderName = '',
    currencyCode = '',
    sortColumn,
    sortOrder,
  }: IGetBeneficiariesParam): Promise<IApiResponse | null> => {
    set({ loading: true });

    try {
      const response = await axiosInstance.get(`/corporate/beneficiaries`, {
        params: {
          page,
          pageSize,
          ...(accountHolderName ? { accountHolderName } : {}),
          ...(currencyCode ? { currencyCode } : {}),
          ...(sortColumn ? { sortColumn } : {}),
          ...(sortOrder ? { sortOrder } : {}),
        },
      });

      if (response?.data?.value) {
        get().setBeneficiaryPagination({
          currentPage: response.data.value.page,
          totalRecords: response.data.value.totalCount,
          currentPageSize: response.data.value.pageSize,
          hasNextPage: response.data.value.hasNextPage,
          hasPrevious: response.data.value.hasPrevious,
        });
        get().setBeneficiaries(response.data.value.items);
      }

      return response.data;
    } catch (err: any) {
      errorHandler(err);
      return {
        value: null,
        isSuccess: false,
        isFailure: true,
        error: err,
      };
    } finally {
      set({ loading: false });
    }

    return null;
  },
  deleteBeneficiary: async (id: string) => {
    set({ loading: true });
    try {
      const response = await axiosInstance.delete(
        `/corporate/delete-beneficiary`,
        { data: { id } },
      );

      await get().getAllBeneficiary({
        page: 1,
        pageSize: 10,
        accountHolderName: '',
        currencyCode: '',
      });

      set({ loading: false });
      return response.data;
    } catch (err: any) {
      errorHandler(err);
      set({ loading: false });
      return null;
    }
  },
  validateIban: async (iban: string) => {
    set({ loading: true });
    try {
      const response = await axiosInstance.get(`/iban/${iban}`);

      set({ loading: false });
      return response.data.value;
    } catch (err: any) {
      errorHandler(err);
      set({ loading: false });
      return null;
    }
  },
  validateRoutingNumber: async (routingNumber: string) => {
    set({ loading: true });
    try {
      const response = await axiosInstance.get(`/routing/${routingNumber}`);

      set({ loading: false });
      return response.data.value;
    } catch (err: any) {
      errorHandler(err);
      set({ loading: false });
      return null;
    }
  },
  validateSwiftCode: async (swiftCode: string) => {
    set({ loading: true });
    try {
      const response = await axiosInstance.get(`/swiftcode/${swiftCode}`);

      set({ loading: false });
      return response.data.value;
    } catch (err: any) {
      errorHandler(err);
      set({ loading: false });
      return null;
    }
  },
}));

export default useBeneficiaryStore;
